// Product/variant resolution utility
// Resolves ambiguous inputs (variant ID, product handle) to concrete variant data
// via Shopify AJAX API (/products/{handle}.js) with persistent localStorage cache

import {
	getCachedProduct,
	getCachedProductById,
	getCachedProductByVariantId,
	cacheProduct as persistProduct,
} from '@/scripts/core/product-cache'

export type ShopifyProductJSON = {
	id: number
	title: string
	handle: string
	type: string
	featured_image?: string
	images?: string[]
	variants: ShopifyVariantJSON[]
}

export type ShopifyVariantJSON = {
	id: number
	available: boolean
	sku: string
	price: number
	compare_at_price: number | null
	title: string
	featured_image?: { src: string } | null
}

export type ResolvedProduct = {
	product: ShopifyProductJSON | null
	variant: ShopifyVariantJSON
}

// In-memory caches (session-only, no cap — persistent cache handles eviction via TTL)
const productCache = new Map<string, ShopifyProductJSON>()
const productIdCache = new Map<number, ShopifyProductJSON>()

function cacheProductLocally(product: ShopifyProductJSON) {
	productCache.set(product.handle, product)
	productIdCache.set(product.id, product)
	persistProduct(product)
}

export async function fetchProductByHandle(
	handle: string
): Promise<ShopifyProductJSON> {
	// Check in-memory first
	const memCached = productCache.get(handle)
	if (memCached) return memCached

	// Check persistent cache
	const persisted = getCachedProduct(handle)
	if (persisted) {
		productCache.set(handle, persisted)
		productIdCache.set(persisted.id, persisted)
		return persisted
	}

	// Network fetch
	const response = await fetch(`/products/${handle}.js`)
	if (!response.ok) {
		throw new Error(
			`Failed to fetch product "${handle}": ${response.status} ${response.statusText}`
		)
	}

	const product = (await response.json()) as ShopifyProductJSON
	cacheProductLocally(product)
	return product
}

export async function fetchProductById(
	productId: number
): Promise<ShopifyProductJSON | null> {
	// Check in-memory by product ID
	const memCached = productIdCache.get(productId)
	if (memCached) return memCached

	// Check in-memory by variant ID
	for (const product of productIdCache.values()) {
		if (product.variants.some((v) => v.id === productId)) {
			return product
		}
	}

	// Check persistent cache by product ID
	const persisted = getCachedProductById(productId)
	if (persisted) {
		cacheProductLocally(persisted)
		return persisted
	}

	// Check persistent cache by variant ID
	const persistedByVariant = getCachedProductByVariantId(productId)
	if (persistedByVariant) {
		cacheProductLocally(persistedByVariant)
		return persistedByVariant
	}

	// Paginate through /products.json — capped at 2 pages (down from 10)
	const maxPages = 2
	for (let page = 1; page <= maxPages; page++) {
		try {
			const response = await fetch(
				`/products.json?limit=250&page=${page}`
			)
			if (!response.ok) {
				console.warn(`[ProductResolver] /products.json page ${page} returned ${response.status}`)
				return null
			}

			const data = (await response.json()) as {
				products: ShopifyProductJSON[]
			}
			const products = data.products || []

			for (const product of products) {
				cacheProductLocally(product)
			}

			// Check by product ID
			const found = productIdCache.get(productId)
			if (found) return found

			// Check if any product has a variant matching the input ID
			for (const product of products) {
				if (product.variants.some((v) => v.id === productId)) {
					return product
				}
			}

			// No more pages
			if (products.length < 250) break
		} catch (e) {
			console.warn('[ProductResolver] Error fetching /products.json:', e)
			return null
		}
	}

	console.warn(`[ProductResolver] Product ${productId} not found in ${maxPages} pages`)
	return null
}

export function getFirstAvailableVariant(
	product: ShopifyProductJSON
): ShopifyVariantJSON | undefined {
	return product.variants.find((v) => v.available) ?? product.variants[0]
}

export async function resolveProductInput(
	input: number | string
): Promise<ResolvedProduct> {
	if (typeof input === 'number') {
		// Treat as variant ID directly — let Shopify validate on cart/add.js
		return {
			product: null,
			variant: { id: input, available: true, sku: '', price: 0, compare_at_price: null, title: '' },
		}
	}

	// String → product handle
	const product = await fetchProductByHandle(input)
	const variant = getFirstAvailableVariant(product)
	if (!variant) {
		throw new Error(`No variants found for product "${input}"`)
	}

	return { product, variant }
}

/** Pre-warm cache from an already-fetched product (for external callers). */
export function warmCacheFromProduct(product: ShopifyProductJSON): void {
	cacheProductLocally(product)
}
